import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { EMPTY, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AuthLogout, AuthSetExpiresAt, AuthSetUser } from './auth.actions';
import { AuthState, AuthStateModel } from './auth.state';
import { AuthMeResponse, AuthUserData, LoginData } from './models';
import { HttpErrorResponse } from '@angular/common/http';
import { ProfileSetFromAuthMe } from '../administration';
import { parseUtcDateStringToMs } from './auth.utils';

@Injectable({ providedIn: 'root' })
export class AuthRefreshSchedulerService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private started = false;

  // 2 minutes safety window
  private readonly safetyWindowMs = 2 * 60 * 1000;

  constructor(private store: Store, private authService: AuthService) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.store
      .select(AuthState.getState)
      .pipe(
        map((state: AuthStateModel) => state.expiresAt),
        filter((expiresAt: number): expiresAt is number => typeof expiresAt === 'number' && Number.isFinite(expiresAt)),
        distinctUntilChanged(),
        switchMap((expiresAt: number) => {
          const now = Date.now();
          const dueIn = Math.max(0, expiresAt - now - this.safetyWindowMs);

          return timer(dueIn).pipe(
            switchMap(() => this.authService.refresh()),
            switchMap(() => this.authService.me()),
            tap((loginData: LoginData) => {
              const me = loginData.response;
              const expiresAtMs = parseUtcDateStringToMs(me.expiresAt);

              this.store.dispatch([
                new AuthSetExpiresAt(Number.isFinite(expiresAtMs) ? expiresAtMs : null),
                new AuthSetUser(this.stripExpiresAt(me)),
                new ProfileSetFromAuthMe(loginData.dto),
              ]);
            }),
            catchError((error: unknown) => {
              const status = (error as HttpErrorResponse | undefined)?.status;
              if (status === 401) {
                this.store.dispatch(new AuthLogout());
              }
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private stripExpiresAt(me: AuthMeResponse): AuthUserData {
    const { expiresAt: _expiresAt, ...user } = me;
    return user as AuthUserData;
  }
}
